/* eslint-disable no-restricted-imports */
import {
	clearFreeGiftSelection,
	markFreeGiftSelectionAddedFromPdp,
	notifyFreeGiftAddedFromPdp,
	readStoredFreeGiftSelection,
} from '@/components/blocks/FreeGiftV2/utils';
import { addProductCartSX } from '@/components/blocks/ProductDetails/styles/addProductCart';
import { DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC } from '@/data/constants/dataKey';
import { EMPTY_STRING } from '@/data/constants/marketing';
import { TYPES } from '@/data/constants/product';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { addToCartFetcherV2, BASE_ADD_2_CART_BODY } from '@/data/Content/Cart';
import { personMutatorKeyMatcher } from '@/data/Content/Login';
import { useNotifications } from '@/data/Content/Notifications';
import { usePaintColors } from '@/data/Content/usePaintColors';
import { useSettings } from '@/data/Settings';
import { useColorDrawerState } from '@/data/state/useColorDrawerState';
import { useDrawerState } from '@/data/state/useDrawerState';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { TransactionErrorResponse } from '@/data/types/Basic';
import { PaintColor, paintObject } from '@/data/types/ColorType';
import { ProductType } from '@/data/types/Product';
import { useUser } from '@/data/User';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { processError } from '@/data/utils/processError';
import { getContractIdFromContext } from '@/utils/getContractIdFromContext';
import { cartMutatorKeyMatcher } from '@/utils/mutatorKeyMatchers';
import { productIsA } from '@/utils/productIsA';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import { Button, CircularProgress, useMediaQuery } from '@mui/material';
import { SxProps, Theme, useTheme } from '@mui/material/styles';
import { ComIbmCommerceRestOrderHandlerCartHandlerAddOrderItemBodyDescription } from 'integration/generated/transactions/data-contracts';
import {
	TrackAddToCartItemProps,
	useTrackAddToCart,
} from 'presentation/events/hooks/useTrackAddToCart';
import { useTrackException } from 'presentation/events/hooks/useTrackException';
import { FC, useState } from 'react';
import {
	getFinalColorCode,
	OrderItemInput,
	returnArray,
} from 'shared/custom/utils/colorSharedUtils';
import { useSWRConfig } from 'swr';
import { fetcherForProducts, getProductsSWRKey } from '@/data/Content/Products-Server';
import { getCurrencyParamFromContext } from '@/data/utils/getCurrencyParamFromContext';
import { expand } from '@/data/utils/keyUtil';
import { useDefaultStore } from '@/data/Content/DefaultStore';
import { getStoreDataByPhysicalStoreId } from '@/data/utils/commonIntegrationUtils';

interface Props {
	partNumber?: string | string[];
	quantity: string | string[];
	wording: string;
	manufacturer?: string | string[];
	colorCodeSelected?: any;
	disabled?: boolean;
	attributes?: any | any[];
	handleClose?: () => void;
	paints?: paintObject;
	isIconButton?: boolean;
	productDetails?: ProductType;
	products?: ProductType[];
	productQuantity?: number;
	categories?: string[];
	id?: string;
	fullWidth?: boolean;
	sx?: SxProps<Theme>;
	variant?: 'text' | 'outlined' | 'contained';
	isProductStoreOnlyAvailable?: boolean;
	availableQuantity?: number;
	setShowMsg?: (value: boolean) => void;
	freeProductPartnumber?: string | string[];
	freeProductPromoType?: string;
	className?: string;
	shipModeId?: string;
	homeDeliveryDate?: Date;
	pickupDate?: Date;
}

/**
 * ProductDetails and categories are used for tracking purpose. Only available when adding from PDP.
 * @param props
 * @returns
 */
export const AddProductCart: FC<Props> = (props) => {
	const {
		id = 'addToCart',
		partNumber,
		quantity,
		wording: addToCartText,
		manufacturer,
		attributes,
		handleClose,
		paints,
		isIconButton,
		disabled: disabledButton,
		productDetails,
		products,
		productQuantity,
		categories,
		fullWidth = true,
		sx,
		variant,
		isProductStoreOnlyAvailable,
		availableQuantity = 0,
		setShowMsg,
		freeProductPartnumber,
		freeProductPromoType,
		className,
		shipModeId,
		homeDeliveryDate,
		pickupDate,
	} = props;

	const trackAddToCart = useTrackAddToCart();
	const trackException = useTrackException();

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));
	const { settings } = useSettings();
	const router = useNextRouter();
	const { storeId, langId, defaultCatalogId: catalogId } = getClientSideCommon(settings, router);
	const { user, loading } = useUser();
	const contractId = getContractIdFromContext(user?.context);
	const { mutate } = useSWRConfig();
	const { actions: drawerActions } = useDrawerState();
	const [isLoading, setLoading] = useState(false);
	const { notifyError, notifyAddToCart } = useNotifications();
	const requestParams = useExtraRequestParameters();
	const { colorDrawerState: hdmDrawerColorData } = useColorDrawerState();
	const { partNumber: partNumberSelected, colorCode, colorCodeSelected } = hdmDrawerColorData;
	const { value: baseValue } = (colorCodeSelected as PaintColor) || '';
	const { storeLocator } = useStoreLocatorState();
	const { colorClassification, lineRangeColors } = usePaintColors();

	// TODO: This needs to be migrated to a state hook if still needed.
	const installationPartNumberData = '';
	const { physicalStoreId } = useDefaultStore();
	const { marketId, stLocId } = getStoreDataByPhysicalStoreId(physicalStoreId);

	const handleAddToCart = async () => {
		const currentQty = productQuantity ?? 1;
		const isInStock = currentQty <= availableQuantity;
		if (!isInStock && setShowMsg) {
			// Show Msg
			setShowMsg(true);

			setTimeout(() => {
				setShowMsg(false);
			}, 5000);
			return;
		}
		setLoading(true);
		drawerActions.openCartDrawer();

		try {
			const orderItems: OrderItemInput[] = [];
			// 1. Prepare main product(s)
			if (partNumber) {
				const pns = returnArray(partNumber);
				const mfs = returnArray(manufacturer);
				const qtys = returnArray(quantity);
				const atts = returnArray(attributes);

				pns.forEach((pn, index) => {
					orderItems.push({
						partNumber: pn,
						quantity: qtys[index] || qtys[0],
						manufacturer: mfs[index] || mfs[0],
						attributes: atts[index] || atts[0],
						contractId: getContractIdFromContext(user?.context),
					});
				});
			}

			// 1.5. Fold in a completed FreeGiftV2 promotion selection saved for this product, if any
			const freeGiftPartNumber = productDetails?.partNumber ?? returnArray(partNumber)[0];
			const storedFreeGiftSelection = freeGiftPartNumber
				? readStoredFreeGiftSelection(freeGiftPartNumber)
				: undefined;

			if (storedFreeGiftSelection) {
				storedFreeGiftSelection.selectedPurchaseSkus.forEach(({ sku, quantity: skuQuantity }) => {
					const mainOrderItem = orderItems.find((item) => item.partNumber === sku);

					if (mainOrderItem) {
						mainOrderItem.quantity = String(skuQuantity);
					} else {
						orderItems.push({
							partNumber: sku,
							quantity: String(skuQuantity),
							contractId: getContractIdFromContext(user?.context),
						});
					}
				});

				storedFreeGiftSelection.selectedRewardSkus.forEach(({ sku, quantity: skuQuantity }) => {
					orderItems.push({
						partNumber: sku,
						quantity: String(skuQuantity),
						contractId: getContractIdFromContext(user?.context),
					});
				});
			}

			// 2. TODO : Prepare installation product
			if (installationPartNumberData) {
				orderItems.push({
					partNumber: installationPartNumberData,
					quantity: quantity[0] || '1',
				});
			}

			// 3. Prepare paint products
			if (paints && paints.partNumbers.length > 0) {
				paints.partNumbers.forEach((paintPn, index) => {
					orderItems.push({
						partNumber: paintPn,
						quantity: String(paints.quantities[index]),
						manufacturer: paints.manufacturer[index],
						attributes: paints.attributes[index],
						contractId: getContractIdFromContext(user?.context),
					});
				});
			}

			if (orderItems.length === 0) {
				setLoading(false);
				return;
			}

			// 4. Calculate color codes and create payload for API
			const orderItemsPayload = orderItems.map((item) => ({
				partNumber: item.partNumber,
				quantity: item.quantity,
				contractId: item.contractId,
				xitem_colorCode:
					getFinalColorCode(
						item,
						colorClassification,
						lineRangeColors,
						partNumberSelected ?? '',
						colorCode ?? '',
						baseValue ?? ''
					) ?? undefined,
				...(shipModeId ? { shipModeId } : {}),
			}));

			// 5. Add to cart
			const body = {
				...BASE_ADD_2_CART_BODY,
				x_pstore: storeLocator.selectedStore?.id ?? undefined,
				orderItem: orderItemsPayload,
			} as ComIbmCommerceRestOrderHandlerCartHandlerAddOrderItemBodyDescription;

			await addToCartFetcherV2(user?.isGeneric)(storeId, { contractId }, body, requestParams);
			if (user?.isGeneric) {
				mutate(personMutatorKeyMatcher(EMPTY_STRING)); // current page
				mutate(personMutatorKeyMatcher(DATA_KEY_E_SPOT_DATA_FROM_NAME_DYNAMIC), undefined);
			}
			// 6. Analytics triggers: now that we have all data, fire the analytics events.
			const freeGiftPartNumbers = [
				...returnArray(freeProductPartnumber),
				...(storedFreeGiftSelection?.selectedRewardSkus.map(({ sku }) => sku) ?? []),
			];
			const promoThdCode = freeProductPromoType ?? storedFreeGiftSelection?.promoThdCode;

			const buildTrackAddToCartItems = ({
				products,
			}: {
				products: ProductType[];
			}): TrackAddToCartItemProps[] => {
				const items = products.reduce((acc, product) => {
					const orderItem = orderItems.find((item) => item.partNumber === product.partNumber);

					if (!orderItem) return acc;

					const isGift = freeGiftPartNumbers.includes(product.partNumber);

					return [
						...acc,
						{
							product: {
								...product,
								isGift,
								freeQty: isGift ? orderItem.quantity : undefined,
								...(isGift ? { promoThdCode } : {}),
							},
							quantity: orderItem.quantity,
						},
					];
				}, [] as TrackAddToCartItemProps[]);

				return items;
			};

			const trackAddToCartForMultipleProducts = async () => {
				let items: TrackAddToCartItemProps[];
				if (!products || (products && orderItems.length > 1)) {
					const partNumbers = orderItems?.map((item) => item.partNumber);
					if (!partNumbers?.length) {
						return undefined;
					}

					const productsFetcher = fetcherForProducts(true);

					const key = getProductsSWRKey({
						storeId,
						lookupKey: 'partNumber',
						lookupValue: partNumbers,
						catalogId,
						langId,
						physicalStoreId,
						marketId,
						stLocId,
						...getCurrencyParamFromContext(user?.context),
					});

					const response = await productsFetcher(expand(key), requestParams);

					const products = response
						.map((item) => item.product)
						.filter((product): product is ProductType => product !== null);
					if (!products) return;
					items = buildTrackAddToCartItems({
						products,
					});
				} else {
					if (!products) return;
					items = buildTrackAddToCartItems({
						products,
					});
				}

				if (!items.length) return;

				trackAddToCart({
					items,
					homeDeliveryDate,
					pickupDate,
					isEstimatedDeliveryDateDisplayed: 'homeDeliveryDate' in props && 'pickupDate' in props,
				});
			};

			const trackAddToCartForBundle = () => {
				const isBundle = productIsA(productDetails, TYPES.bundle);

				if (!isBundle) {
					return;
				}

				// / TODO: Implement bundle logic
			};

			const trackAddToCartForKit = () => {
				if (!productDetails) return;

				const isKit = productIsA(productDetails, TYPES.kit);

				if (!isKit) return;

				const components = productDetails.components ?? [];

				const items = buildTrackAddToCartItems({
					products: components,
				});

				if (!items.length) return;

				trackAddToCart({
					categories,
					product: {
						...productDetails,
						isGift: freeGiftPartNumbers.includes(productDetails.partNumber),
					},
					quantity: productQuantity ?? 1,
					homeDeliveryDate,
					pickupDate,
					isEstimatedDeliveryDateDisplayed: 'homeDeliveryDate' in props && 'pickupDate' in props,
				});
			};

			const trackAddToCartForProduct = () => {
				// For the rest of product types

				if (!productDetails) return;

				if (!productIsA(productDetails, [TYPES.product, TYPES.sku, TYPES.variant])) {
					return;
				}

				const orderItem = orderItems.find((item) => item.partNumber === productDetails.partNumber);

				if (!orderItem) return;

				const isGift = freeGiftPartNumbers.includes(productDetails.partNumber);

				trackAddToCart({
					categories,
					product: {
						...productDetails,
						isGift,
						freeQty: isGift ? orderItem.quantity : undefined,
						...(isGift ? { promoThdCode } : {}),
					},
					quantity: orderItem.quantity,
					homeDeliveryDate,
					pickupDate,
					isEstimatedDeliveryDateDisplayed: 'homeDeliveryDate' in props && 'pickupDate' in props,
				});
			};

			if (products || orderItems.length > 1) {
				trackAddToCartForMultipleProducts();
			} else {
				trackAddToCartForBundle();
				trackAddToCartForKit();
				trackAddToCartForProduct();
			}

			if (handleClose) handleClose();
			await mutate(cartMutatorKeyMatcher(''));
			notifyAddToCart();

			if (storedFreeGiftSelection && freeGiftPartNumber) {
				if (storedFreeGiftSelection.isDirect) {
					markFreeGiftSelectionAddedFromPdp(freeGiftPartNumber);
				} else {
					clearFreeGiftSelection(freeGiftPartNumber);
				}

				notifyFreeGiftAddedFromPdp(freeGiftPartNumber);
			}
		} catch (e) {
			notifyError(processError(e as TransactionErrorResponse));
			trackException(e);
		} finally {
			setLoading(false);
		}
	};

	const isDisabledWithPaint = () => {
		if (paints && paints?.quantities.length > 0) {
			return false;
		} else {
			return quantity.length === 0 || quantity === '0';
		}
	};

	const isDisabledBtn =
		disabledButton ||
		isDisabledWithPaint() ||
		isLoading ||
		isProductStoreOnlyAvailable ||
		availableQuantity <= 0;

	return (
		<>
			{isIconButton ? (
				<Button
					id={
						isProductStoreOnlyAvailable
							? 'storeExclusiveButton'
							: isDisabledBtn
							? 'notAvailableButton'
							: id
					}
					variant={variant ? variant : 'contained'}
					sx={addProductCartSX.addButton}
					fullWidth={fullWidth}
					onClick={handleAddToCart}
					disabled={loading || isDisabledBtn}
					className={className}
				>
					{isLoading ? (
						<CircularProgress size={24} color="inherit" />
					) : !isDisabledBtn && isMobile ? (
						<AddShoppingCartIcon />
					) : (
						addToCartText
					)}
				</Button>
			) : (
				<Button
					id={id}
					variant={variant ? variant : 'outlined'}
					fullWidth={fullWidth}
					onClick={handleAddToCart}
					disabled={loading || isDisabledBtn}
					sx={sx}
					className={className}
				>
					{isLoading ? <CircularProgress size={24} color="inherit" /> : addToCartText}
				</Button>
			)}
		</>
	);
};
